import React, {
  useMemo,
  ReactNode,
  createContext,
  useContext,
  useEffect,
} from "react";

import { Version, useVersionsQuery } from "@/domain/Version";
import {
  ComplexFirestoreQuerySetting,
  FirestoreQuerySetting,
} from "@/firebase/firestore";
import { usePermissions } from "../PermissionsProvider";

const versionsCtx = createContext<{
  versions: Version[];
  isLoadedVersions: boolean;
}>({
  versions: [],
  isLoadedVersions: false,
});

// 認可されたバージョン一覧を取得し各所で提供するプロバイダ
export const VersionsProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [versions, isLoadedVersions] = useAuthorizedVersions();
  const versionsCtxValue = useMemo(
    () => ({
      versions,
      isLoadedVersions,
    }),
    [versions, isLoadedVersions]
  );

  return (
    <versionsCtx.Provider value={versionsCtxValue}>
      {children}
    </versionsCtx.Provider>
  );
};

export const useVersions = () => {
  return useContext(versionsCtx);
};

// 自身のロールで許可されたバージョンのみを取得する
export const useAuthorizedVersions = (
  options: Parameters<typeof useVersionsQuery>[2] = {}
): [Version[], boolean] => {
  const { me, isLoadedMe } = usePermissions();
  const { permissions } = me || {};
  const hasVersionsReadPermission = permissions?.versions.read;
  const authorizedCategoryIds = permissions?.versions_version_category.read;
  const authorizedVersionIds = permissions?.version.read;

  // permissions ロード前は全て undefined になりクエリを正しく組み立てられない。
  // また list 権限を 1 件も持たないユーザーは Firestore Rules で list 自体が拒否される。
  // いずれのケースでも購読を発行すると permission-denied が発生するので、
  // enabled フラグで初回購読自体をスキップする。
  const hasAnyVersionsListPermission =
    !!hasVersionsReadPermission ||
    !!authorizedCategoryIds?.length ||
    !!authorizedVersionIds?.length;
  const enabled = isLoadedMe && hasAnyVersionsListPermission;

  const query: ComplexFirestoreQuerySetting | null = hasVersionsReadPermission
    ? null
    : {
        logicalOp: "or",
        ops: [
          ...(authorizedCategoryIds?.map((category) => ({
            fieldPath: "category",
            opStr: "==" as const,
            value: category,
          })) ?? []),
          ...(authorizedVersionIds?.map(
            (version) =>
              ({
                // NOTE: "__name__" は本来 firebase.firestore.documentId() が最終的に返す値
                //       documentId()を使おうとすると修正範囲が広がりすぎるため, ここではそのまま使っている
                fieldPath: "__name__",
                opStr: "==" as const,
                value: version,
              }) as FirestoreQuerySetting<string>
          ) ?? []),
        ],
      };

  const [versions, isLoadedVersions, _querySetting, changeQuerySetting] =
    useVersionsQuery({}, query === null ? undefined : async () => query, {
      ...options,
      enabled,
    });

  useEffect(() => {
    // enabled が false のうちに changeQuerySetting を呼ぶと、permissions ロード後に
    // enabled が true になった瞬間の useItemsQuery 側 useEffect で stale な querySetting
    // (空 ops の OR → フィルタ無しの全件 list と等価) で購読されてしまい permission-denied になる。
    // enabled が true のときだけ最新クエリを反映する。
    if (!enabled) return;
    changeQuerySetting(query);
  }, [permissions, enabled]);

  return [versions, isLoadedMe && isLoadedVersions];
};
